"use client"
import {
  collection,
  DocumentSnapshot,
  onSnapshot,
  Unsubscribe,
  writeBatch,
} from "firebase/firestore"
import { Timestamp, updateDoc } from "firebase/firestore"
import { doc, getDoc, setDoc } from "firebase/firestore"
import { orderBy, query, where } from "firebase/firestore"
import { getDocs, getFirestore } from "firebase/firestore"
import { increment } from "firebase/firestore"
import { useEffect, useState } from "react"
import { getUser } from "services/auth"
import { getInspection } from "./Inspection"
import { getPreferences } from "services/Preferences"
import dexie, { getActiveInspection, updateInspection, ensureDb } from "@/database"
import { v4 } from "uuid"
import MESSAGE from "lang/messages"
import Keypath from "utils/keypath"
import Time from "@/utils/time"

type Progress =
  | { count?: number; done: number }
  | { count: number; done?: number }
type UpdateProgressType = {
  section: string
  action: "increment" | "decrement"
  count?: number
}
type Generic = {
  [key: string]: { index: number;[key: string]: any }
}
type Fn = (m: string) => void
function transform(object: Generic) {
  const unsorted: Record<string, any>[] = []
  for (let key in object) {
    unsorted.push({ id: key, ...object[key] })
  }

  return unsorted.sort((a, b) => a.index - b.index)
}
function trasformItems(items: { [key: string]: LayoutItem }) {
  const unsorted = []
  for (let key in items) {
    const comments = transform(items[key].comments)
    unsorted.push({ ...items[key], id: key, comments })
  }
  return unsorted.sort((a, b) => a.index - b.index)
}
function transformSections(sections: { [key: string]: LayoutSection }) {
  const unsorted = []
  for (let key in sections) {
    const items = trasformItems(sections[key].items)
    unsorted.push({ ...sections[key], id: key, items })
  }
  return unsorted.sort((a, b) => a.index - b.index)
}
export async function createLayout(id: string, fn: Fn = () => { }) {
  fn(MESSAGE.layoutCreation)
  const inspection = await getInspection(id)

  if (!inspection) throw new Error(`${id} ${MESSAGE.inspectionNotFound}`)

  const layouts = await getLayoutList()
  const layout = layouts[0]
  delete layout.author
  layout.modified = Timestamp.now()
  layout.id = id
  const db = getFirestore()

  const retryPut = async () => {
    await ensureDb()
    await dexie.table("layouts").put(layout)
  }

  try {
    await retryPut()
  } catch (error: any) {
    if (error.name === "DatabaseClosedError") {
      await retryPut()
    } else {
      throw error
    }
  }

  setDoc(doc(db, `layouts/${id}`), layout).catch(console.error)
  return layout
}
export type LayoutItemComments = {
  id: string
  name: string
  index: number
  hidden?: boolean
  comments: Field[]
}
export type TransformedItem = {
  id: string
  name: string
  index: number
  hidden?: boolean
  comments: Field[]
}
export type TransformedSection = {
  id: string
  name: string
  index: number
  hidden?: boolean
  items: TransformedItem[]
  progress: { count: number; done: number }
}
export type LayoutType = {
  id: string
  author?: string
  label: string
  modified: Timestamp
  version: number
  sections: { [key: string]: LayoutSection }
}
export type LayoutSection = {
  id: string
  name: string
  index: number
  hidden?: boolean
  progress: { count: number; done: number }
  items: { [key: string]: LayoutItem }
}
export type LayoutItem = {
  id: string
  name: string
  index: number
  hidden?: boolean
  comments: { [key: string]: Field }
}
export type Field = {
  id: string
  name: string
  index: number
  origin?: string
  type?: "checkbox" | "radio" | "input" | "textarea"
}

export type v3Layout = {
  id: string
  version: number
  conditions: { name: string; comments: { id: string; name: string } }[]
  sections: {
    id: string
    name: string
    items: {
      id: string
      nid?: string
      name: string
      comments: {
        id: string
        nid?: string
        name: string
      }[]
      hidden: boolean
    }[]
    index: number
    comments: number
    hidden: boolean
  }[]
}
export async function upgradeLayout(layout: v3Layout, type: string) {
  console.log("upgrade")

  const db = getFirestore()
  const document = doc(db, `layouts/${layout.id}`)
  const sections: { [key: string]: LayoutSection } = {}

  for (let s = 0; s < layout.sections.length; s++) {
    const { id, name } = layout.sections[s]

    const section: LayoutSection = {
      id,
      name,
      index: s,
      progress: { count: 0, done: 0 },
      items: {},
    }

    for (let i = 0; i < layout.sections[s].items.length; i++) {
      const { id, name } = layout.sections[s].items[i]

      const item: LayoutItem = { id, name, index: i, comments: {} }

      for (let c = 0; c < layout.sections[s].items[i].comments.length; c++) {
        const { id, name } = layout.sections[s].items[i].comments[c]
        section.progress.count++
        item.comments[id] = { id, name, index: c }
      }
      section.items[id] = item
    }
    sections[id] = section
  }
  const newLayout: LayoutType = {
    id: layout.id,
    label: "Standard",
    modified: Timestamp.now(),
    version: 4,
    sections,
  }

  updateInspection(layout.id, { type })
  await setDoc(document, newLayout, { merge: true })
  return newLayout
}
/**
 * Get default/custom layouts
 * custom and default layouts all have a auther property
 * which can be either the current inspectors uid or admin.
 * this function gets both and stores them locally.
 */
export async function downloadLayouts() {
  const { offline_mode } = await getPreferences()
  const user = await getUser()
  if (!user) throw new Error("Unauthenticated user access")

  const w = where("author", "in", [user.uid, "admin"])
  const q = query(collection(getFirestore(), "layouts"), w)
  const snapshots = await getDocs(q)

  if (snapshots.empty) throw new Error("No layouts present")
  const layouts = snapshots.docs.map(snapshot => ({
    id: snapshot.id,
    ...snapshot.data(),
  })) as LayoutType[]
  await ensureDb()
  await dexie.table("layouts").bulkPut(layouts)
  return layouts
}
//Get a list of all layouts created by user/admin
export async function getLayoutList() {
  const user = await getUser()

  if (!user) throw new Error("Unauthenticated user access")

  await ensureDb()
  const cached = await dexie
    .table("layouts")
    .where("author")
    .anyOf([user.uid, "admin"])
    .toArray()

  if (cached.length) return cached

  const w = where("author", "in", [user.uid, "admin"])
  const q = query(collection(getFirestore(), "layouts"), w)
  const snapshots = await getDocs(q)
  if (snapshots.empty) throw new Error("No layouts present")

  const layouts = snapshots.docs.map(snapshot => ({
    id: snapshot.id,
    ...snapshot.data(),
  })) as LayoutType[]
  await dexie.table("layouts").bulkPut(layouts)
  return layouts
}
export async function getV3Layout(id: string) {
  const _layout = doc(getFirestore(), `layouts/${id}`)
  const snapshot = await getDoc(_layout)
  if (!snapshot.exists()) return null
  if (!snapshot.data().version) return null
  return { id: snapshot.id, ...snapshot.data() } as v3Layout
}
export async function getLayout(
  id: string,
  fn: () => void = () => { },
): Promise<LayoutType> {
  let timeout: any
  const { offline_mode } = await getPreferences()

  if (offline_mode) {
    await ensureDb()
    const layout = await dexie.table("layouts").get(id)

    if (layout) return layout
    throw new Error("Layout not found in local database")
  }
  const timeoutPromise = new Promise((res, rej) => {
    timeout = setTimeout(async () => {
      clearTimeout(timeout)
      //get layout from dexie if firestore is taking too long
      await ensureDb()
      const layout = await dexie.table("layouts").get(id)

      if (layout) return res(layout)
      rej(new Error("Layout not found in local database"))
    }, 1500)
  })

  const document = doc(getFirestore(), `layouts/${id}`)
  //if layout is not in dexie, fetch from firestore

  const snapshot = (await Promise.any([getDoc(document), timeoutPromise])) as
    | DocumentSnapshot
    | LayoutType

  clearTimeout(timeout)
  if ("sections" in snapshot) {
    //if layout was fetched from dexie
    return snapshot
  }
  if (!snapshot.exists()) {
    return null as any
  }

  const layout = { id: snapshot.id, ...snapshot.data() } as LayoutType

  // Timestamp check: only update local cache if remote is newer
  await ensureDb()
  const existingLocal = await dexie.table("layouts").get(layout.id)
  if (existingLocal && existingLocal.modified && layout.modified) {
    const localTime = Time.toMillis(existingLocal.modified)
    const remoteTime = Time.toMillis(layout.modified)
    if (localTime >= remoteTime) return existingLocal
  }

  await dexie.table("layouts").put(layout)
  return layout
}
export async function syncLocalLayoutToFirebase(id: string) {
  await ensureDb()
  const localLayout = await dexie.table("layouts").get(id)
  if (!localLayout) return

  const db = getFirestore()
  const document = doc(db, `layouts/${id}`)
  const snapshot = await getDoc(document)

  if (!snapshot.exists()) {
    console.log(`Syncing layout ${id} to Firestore (missing online)`)
    await setDoc(document, localLayout)
  } else {
    // Document exists: sync local changes if local is newer
    const remote = { id: snapshot.id, ...snapshot.data() } as LayoutType
    if (localLayout.modified && remote.modified) {
      const localTime = Time.toMillis(localLayout.modified)
      const remoteTime = Time.toMillis(remote.modified)
      if (localTime > remoteTime) {
        console.log(`Local layout ${id} is newer, syncing to Firestore`)
        await updateDoc(document, localLayout)
      }
    }
  }
}
export async function onLayout(
  fn: (layout: LayoutType) => void,
  onChange: (comment: LayoutType) => void = () => { },
  id: string = getActiveInspection(),
  onError?: (err: Error) => void
) {
  let isFetching = true

  let unsubscribe = () => { }

  // 1. Immediately serve from local Dexie cache (prevents skeleton flicker on re-visit)
  await ensureDb()
  const local = await dexie.table("layouts").get(id) as LayoutType | undefined
  if (local) {
    fn(local)
    isFetching = false
  }

  // 2. Setup Firestore listener in the background
  syncLocalLayoutToFirebase(id).catch(console.error)

  const document = doc(getFirestore(), `layouts/${id}`)
  unsubscribe = onSnapshot(
    document,
    async snapshot => {
      if (!snapshot.exists()) {
        // Only throw if we also have no local data
        if (!local) {
          const err = new Error("Layout not found in firestore")
          if (onError) onError(err)
          else throw err
        }
        return
      }

      const data = snapshot.data()
      const remote = { id: snapshot.id, ...data } as LayoutType

      // Timestamp validation - don't overwrite newer local data with older remote data
      const currentLocal = (await dexie
        .table("layouts")
        .get(remote.id)) as LayoutType | undefined
      if (currentLocal && currentLocal.modified) {
        const localTime = Time.toMillis(currentLocal.modified)
        const remoteTime = remote.modified ? Time.toMillis(remote.modified) : 0

        // If remote is missing a timestamp or is older than or equal to local, ignore it.
        if (localTime >= remoteTime) {
          // Local is newer or same: sync up to server if different, but don't overwrite locally
          if (isFetching) {
            fn(currentLocal)
            isFetching = false
          } else if (
            !snapshot.metadata.hasPendingWrites &&
            localTime > remoteTime
          ) {
            updateDoc(document, currentLocal as any).catch(console.error)
          }
          return
        }
      }

      // Remote is newer: update local cache and call fn
      await dexie.table("layouts").put(remote)
      fn(remote)
      if (!isFetching) onChange(remote)
      isFetching = false
    },
    error => {
      console.error("Layout listener error:", error)
      if (onError) onError(error)
    },
  )

  // If we had no local cache, check Firestore synchronously before returning
  if (!local) {
    const snapshot = await getDoc(document)
    if (!snapshot.exists()) {
      unsubscribe()
      const err = new Error("Layout not found in firestore")
      if (onError) onError(err)
      else throw err
    }
  }

  return unsubscribe
}

export async function getSections(
  id: string = getActiveInspection(),
  fn: () => void = () => { },
) {
  const layout = await getLayout(id, fn)
  if (!layout) return []

  return transformSections(layout.sections) as TransformedSection[]
}

export function onSections(
  fn: (sections: TransformedSection[]) => void,
  id: string = getActiveInspection(),
  onError?: (err: Error) => void
) {
  return onLayout(({ sections }) => {
    fn(transformSections(sections) as TransformedSection[])
  }, () => { }, id, onError)
}

export async function getItems(
  path: string,
  fn: (items: LayoutItemComments[], section: string) => void,
) {
  try {
    const [id, section] = path.split("/")
    const layout = await getLayout(id)
    if (!layout) return []

    // Guard: section may not exist if the layout was updated
    if (!layout.sections[section]) {
      console.warn(`getItems: section "${section}" not found in layout`)
      return []
    }

    const { items, name } = layout.sections[section]

    fn(trasformItems(items) as LayoutItemComments[], name)
    return trasformItems(items) as LayoutItemComments[]
  } catch (error) {
    throw error
  }
}
export function onItems(
  section: string,
  fn: (items: LayoutItemComments[], section: string) => void,
) {
  return onLayout(layout => {
    // Guard: section may not exist if layout was updated while subscribed
    if (!layout.sections || !layout.sections[section]) {
      console.warn(`onItems: section "${section}" not found in layout — skipping render`)
      return
    }
    const { name, items } = layout.sections[section]
    fn(trasformItems(items) as LayoutItemComments[], name)
  })
}
export async function getSectionPaths(id: string, section: string) {
  const layout = await getLayout(id)
  const paths: string[] = []
  const items = trasformItems(
    layout.sections[section].items,
  ) as LayoutItemComments[]

  items.forEach(item => {
    const { comments } = item

    comments.forEach(comment => {
      paths.push(`${id}/${section}/${item.id}/${comment.id}`)
    })
  })
  return paths
}
export function useGetLayouts() {
  const [layouts, setLayouts] = useState<LayoutType[]>([])
  useEffect(() => {
    downloadLayouts().then(setLayouts)
  }, [])

  return layouts
}
export function useGetLayoutList() {
  const [layouts, setLayouts] = useState<LayoutType[] | null>(null)
  useEffect(() => {
    getLayoutList().then(setLayouts)
  }, [])

  return layouts
}
export function useGetLayout(id: string = getActiveInspection()) {
  const [layout, setLayout] = useState<LayoutType | null>()
  useEffect(() => {
    if (layout) return
    getLayout(id).then(setLayout)
  }, [layout])

  return layout
}
export function useGetSections(
  id: string = getActiveInspection(),
  fn: () => void = () => { },
) {
  const [sections, setSections] = useState<TransformedSection[]>()

  useEffect(() => {
    if (sections) return
    getSections(id, fn).then(sections => {
      setSections(sections as TransformedSection[])
    })
  }, [sections])
  return sections
}
export function useGetItems(path: string, fn: () => void = () => { }) {
  const [items, setItem] = useState<LayoutItemComments[]>()

  useEffect(() => {
    if (items) {
      fn()
      return
    }
    getItems(path, () => { }).then(result => {
      if (result) setItem(result as LayoutItemComments[])
    })
  }, [items])

  return items
}
export async function rename(path: string, name: string) {
  if (!name || name.trim() === "") return
  const id = getActiveInspection()
  const layout = await dexie.table("layouts").get(id)
  if (!layout) return

  const db = getFirestore()
  const document = doc(db, `layouts/${id}`)
  const modified = Timestamp.now()

  Keypath.set({ path, value: name, target: layout })
  layout.modified = modified

  const retryPut = async () => {
    await ensureDb()
    await dexie.table("layouts").put(layout)
  }

  try {
    await retryPut()
  } catch (error: any) {
    if (error.name === "DatabaseClosedError") {
      await retryPut()
    } else {
      console.error("Failed to save renamed layout:", error)
    }
  }

  // Optimistic Firestore Update triggers latency compensation in listeners
  updateDoc(document, { [path]: name, modified }).catch(console.error)
}
export async function hideSection(section: string, hidden = true) {
  const id = getActiveInspection()
  const layout = await getLayout(id)
  if (!layout) return []

  const db = getFirestore()
  const document = doc(db, `layouts/${id}`)
  const modified = Timestamp.now()

  layout.sections[section].hidden = hidden
  layout.modified = modified

  const retryPut = async () => {
    await ensureDb()
    await dexie.table("layouts").put(layout)
  }

  try {
    await retryPut()
  } catch (error: any) {
    if (error.name === "DatabaseClosedError") {
      await retryPut()
    } else {
      console.error("Failed to save hidden section layout:", error)
    }
  }

  // Optimistic Firestore Update
  updateDoc(document, { [`sections.${section}.hidden`]: hidden, modified }).catch(console.error)

  return transformSections(layout.sections) as TransformedSection[]
}
export async function hideItem(path: string, hidden = true) {
  const id = getActiveInspection()
  const layout = await getLayout(id)
  if (!layout) return []

  const db = getFirestore()
  const document = doc(db, `layouts/${id}`)
  const modified = Timestamp.now()

  Keypath.set({ path: `${path}.hidden`, value: hidden, target: layout })
  layout.modified = modified
  const [_, section] = path.split(".")

  const retryPut = async () => {
    await ensureDb()
    await dexie.table("layouts").put(layout)
  }

  try {
    await retryPut()
  } catch (error: any) {
    if (error.name === "DatabaseClosedError") {
      await retryPut()
    } else {
      console.error("Failed to save hidden item layout:", error)
    }
  }

  // Optimistic Firestore Update
  updateDoc(document, { [`${path}.hidden`]: hidden, modified }).catch(console.error)

  return trasformItems(layout.sections[section].items) as LayoutItemComments[]
}
// TODO: implement addSection — add a new section to the active layout
export async function addSection() { }
// TODO: implement addItem — add a new item to a section in the active layout
export async function addItem() { }
//Todo: Validate the randomly generated comment ID because there is a possibility the id already exsist
export async function addComment(path: string, name: string) {
  const id = getActiveInspection()
  const layout = await getLayout(id)
  if (!layout) return

  const db = getFirestore()
  const commentId = v4().split("-")[0]
  const target = Keypath.get(path, layout) as LayoutItem
  const comments = transform(target.comments)
  const comment = { id: commentId, index: comments.length, name }
  const document = doc(db, `layouts/${id}`)
  const modified = Timestamp.now()

  target.comments[commentId] = comment
  layout.modified = modified

  const retryPut = async () => {
    await ensureDb()
    await dexie.table("layouts").put(layout)
  }

  try {
    await retryPut()
  } catch (error: any) {
    if (error.name === "DatabaseClosedError") {
      await retryPut()
    } else {
      console.error("Failed to save added comment layout:", error)
    }
  }

  // Optimistic Firestore Update
  updateDoc(document, { [`${path}.comments.${commentId}`]: comment, modified }).catch(console.error)

  const [_, section] = path.split(".")

  const items = trasformItems(
    layout.sections[section].items,
  ) as LayoutItemComments[]

  const result: { newItems: LayoutItemComments[]; commentID: string } = {
    newItems: items,
    commentID: commentId,
  }

  return result
}
export async function copyItem(path: string, name: string) {
  const id = getActiveInspection()
  const db = getFirestore()
  const batch = writeBatch(db)
  const layout = await getLayout(id)

  const [_, section] = path.split(".")
  const target = Keypath.get(path, layout) as LayoutItem
  const items = trasformItems(layout.sections[section].items)

  const newItem = {
    id: v4().split("-")[0],
    name,
    index: target.index + 1,
    comments: items[target.index].comments.map(
      ({ id, name, index, origin }) => {
        return { id: v4().split("-")[0], name, index, origin: origin || id }
      },
    ),
  }

  items.splice(target.index + 1, 0, newItem)

  const arr = path.split(".")
  arr.pop()
  const newPath = arr.join(".")
  const document = doc(db, `layouts/${id}`)

  items.forEach((item, index) => {
    //set the new item
    if (target.index + 1 === index) {
      let _item = { ...item } as any
      const commentsIdMap: any = {}
      _item.comments.forEach((comment: Field) => {
        commentsIdMap[comment.id] = comment
      })
      _item.comments = commentsIdMap

      batch.update(document, { [`${newPath}.${item.id}`]: _item })
      Keypath.set({ path: `${newPath}.${item.id}`, value: _item, target: layout })
    } else {
      batch.update(document, { [`${newPath}.${item.id}.index`]: index })
      Keypath.set({ path: `${newPath}.${item.id}.index`, value: index, target: layout })
    }
  })

  layout.modified = Timestamp.now()
  const retryPut = async () => {
    await ensureDb()
    await dexie.table("layouts").put(layout)
  }

  try {
    await retryPut()
  } catch (error: any) {
    if (error.name === "DatabaseClosedError") {
      await retryPut()
    } else {
      console.error("Failed to save copied item layout:", error)
    }
  }

  // Use batch.commit() directly — avoids enqueuing the entire layout object in the sync queue
  batch.commit().catch(console.error)

  return items as LayoutItemComments[]
}
export async function copySection(path: string, name: string) {
  const id = getActiveInspection()
  const db = getFirestore()
  const batch = writeBatch(db)
  const layout = await getLayout(getActiveInspection())

  const target = Keypath.get(path, layout) as LayoutSection
  const sections = transformSections(layout.sections)

  const newSection: any = {
    id: v4().split("-")[0],
    name,
    index: target.index + 1,
    progress: sections[target.index].progress,
    items: {},
  }

  sections[target.index].items.forEach(item => {
    const comments: Record<string, any> = {}
    item.comments.forEach(({ id, name, index, origin }) => {
      comments[v4().split("-")[0]] = { name, index, origin: origin || id }
    })
    newSection.items[item.id] = {
      name: item.name,
      index: item.index,
      comments,
    }
  })
  sections.splice(target.index + 1, 0, newSection)

  const updates: any[] = []
  sections.forEach((section, index) => {
    const document = doc(db, `layouts/${id}`)
    if (target.index + 1 === index) {
      let data = { ...section } as any
      delete data.id
      batch.update(document, { [`sections.${section.id}`]: data })
      layout.sections[section.id] = data
    } else {
      batch.update(document, { [`sections.${section.id}.index`]: index })
      layout.sections[section.id].index = index
    }
  })

  layout.modified = Timestamp.now()
  const retryPut = async () => {
    await ensureDb()
    await dexie.table("layouts").put(layout)
  }

  try {
    await retryPut()
  } catch (error: any) {
    if (error.name === "DatabaseClosedError") {
      await retryPut()
    } else {
      console.error("Failed to save copied section layout:", error)
    }
  }

  // Use batch.commit() directly — avoids enqueuing the entire layout object in the sync queue
  batch.commit().catch(console.error)

  return sections as TransformedSection[]
}
